import React, { useMemo, useState } from 'react';
import { Card, Row, Col, Typography, Button, Radio, Modal, Form, Select, message, Tag } from 'antd';
import { EditOutlined, GiftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import Api from '../../../Network';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usePermissions } from '../../../contexts/PermissionContext';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

interface CustomerLoyaltyPointsProps {
  customer: any;
  onUpdate?: () => void;
}

interface LoyaltyPoint {
  id: number;
  customer_type: string;
  customer_category_id: number | string;
  points: number;
  discount_amount: number;
  status: string;
  customer_category?: {
    name_en: string;
    name_ar: string;
  };
}

interface ReceiptLoyaltyPoint {
  id: number;
  points: number;
  used_points?: number;
  expire_at?: string | null;
  created_at?: string;
  expired?: boolean;
  is_expired?: boolean;
}

const CustomerLoyaltyPoints: React.FC<CustomerLoyaltyPointsProps> = ({ customer, onUpdate }) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();

  const [selectedType, setSelectedType] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const currentPoints = customer?.points ?? 0;
  const customerType = customer?.type ?? null;
  const customerCategoryId = customer?.category_id ? customer.category_id.toString() : null;
  const earnedPoints = useMemo<ReceiptLoyaltyPoint[]>(
    () => (Array.isArray(customer?.receipt_loyalties) ? customer.receipt_loyalties : []),
    [customer?.receipt_loyalties]
  );

  // Customer types (credit, cash, unassigned)
  const customerTypes = [
    { value: null, label: t('customers.loyalty.unassigned', 'غير معين') },
    { value: 'cash', label: 'كاش' },
    { value: 'credit', label: 'كریدیت' },
    { value: null, label: t('customers.loyalty.unassigned', 'غير معين') },
  ];

  // Static customer categories
  const customerCategories = [
    { id: '2', name_en: 'Silver', name_ar: 'فضي' },
    { id: '1', name_en: 'Gold', name_ar: 'ذهبي' },
    { id: '3', name_en: 'Diamond', name_ar: 'ماسي' },
  ];

  // Fetch loyalty points using React Query
  const { data: loyaltyPointsData } = useQuery({
    queryKey: ['loyalty-points'],
    queryFn: () => Api.get('/api/admin/loyalty'),
    enabled: true,
  });

  const loyaltyPoints = loyaltyPointsData?.data || [];

  const handleEditPoints = () => {
    // Set current customer type as selected
    setSelectedType(customerType ?? null);
    form.setFieldsValue({
      type: customerType ?? null,
      category_id: customerCategoryId ?? null,
      points: currentPoints,
    });
    setIsModalVisible(true);
  };

  const handleTypeChange = (value: string | null) => {
    setSelectedType(value ?? null);
    form.setFieldsValue({ type: value ?? null });
  };

  // Update customer mutation
  const updateCustomerMutation = useMutation({
    mutationFn: (values: any) =>
      Api.put(`/api/admin/customers/${customer.id}`, {
        type: values.type ?? null,
        category_id:
          values.category_id === null ||
          values.category_id === undefined ||
          values.category_id === ''
            ? null
            : values.category_id,
      }),
    onSuccess: () => {
      message.success(t('customers.loyalty.pointsUpdated', 'Loyalty points updated successfully'));
      setIsModalVisible(false);
      // Invalidate and refetch customer data
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customer', customer.id] });
      onUpdate?.();
    },
    onError: (error: any) => {
      message.error(t('common.error', 'Error updating loyalty points'));
      console.error('Error updating customer:', error);
    },
  });

  const handleSubmit = async (values: any) => {
    updateCustomerMutation.mutate(values);
  };

  const getTypeColor = (type: string) => {
    const typeColors: { [key: string]: string } = {
      cash: 'green',
      credit: 'blue',
    };
    return typeColors[type] || 'default';
  };

  const getCategoryColor = (categoryId: string) => {
    const categoryColors: { [key: string]: string } = {
      '2': 'silver', // Gold
      '1': 'gold', // Silver
      '3': 'purple', // Diamond
    };
    return categoryColors[categoryId] || 'blue';
  };

  const getCurrentLoyaltyPoint = () => {
    if (!customerType || !customerCategoryId) {
      return null;
    }
    return loyaltyPoints.find(
      (lp: LoyaltyPoint) =>
        lp.customer_type === customerType &&
        lp.customer_category_id.toString() === customerCategoryId
    );
  };

  const currentLoyaltyPoint = getCurrentLoyaltyPoint();

  return (
    <>
      <Card className="loyalty-card">
        <div className="loyalty-header">
          <Title level={4} style={{ margin: 0 }}>
            <GiftOutlined style={{ marginRight: 8 }} />
            {t('customers.loyalty.title', 'نقاط داريف')}
          </Title>
          {hasPermission('customers.update_loyalty_points') && (
            <Button type="primary" icon={<EditOutlined />} size="small" onClick={handleEditPoints}>
              {t('common.edit', 'Edit')}
            </Button>
          )}
        </div>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={8}>
            <div className="loyalty-info">
              <Text strong>{t('customers.loyalty.currentPoints', 'Current Points')}</Text>
              <div className="points-display">
                <span className="points-value">{currentPoints}</span>
                <span className="points-label">{t('customers.loyalty.points', 'Points')}</span>
              </div>
            </div>
          </Col>
          <Col span={8}>
            <div className="loyalty-info">
              <Text strong>{t('customers.loyalty.customerType', 'Customer Type')}</Text>
              <div>
                <Tag color={customerType ? getTypeColor(customerType) : 'default'}>
                  {customerType || t('customers.loyalty.unassigned', 'غير معين')}
                </Tag>
              </div>
            </div>
          </Col>
          <Col span={8}>
            <div className="loyalty-info">
              <Text strong>{t('customers.loyalty.category', 'Category')}</Text>
              <div>
                <Tag color={customerCategoryId ? getCategoryColor(customerCategoryId) : 'default'}>
                  {customerCategoryId
                    ? customerCategories.find((cat) => cat.id === customerCategoryId)?.name_ar ||
                      customerCategories.find((cat) => cat.id === customerCategoryId)?.name_en ||
                      customerCategoryId
                    : t('customers.loyalty.unassigned', 'غير معين')}
                </Tag>
              </div>
            </div>
          </Col>
        </Row>

        {currentLoyaltyPoint && (
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col span={24}>
              <div className="loyalty-info">
                <Text strong>{t('customers.loyalty.loyaltyRule', 'Loyalty Rule')}</Text>
                <div>
                  <Text>
                    {t(
                      'customers.loyalty.pointsForDiscount',
                      '{{points}} points for {{amount}} ر.س discount',
                      {
                        points: currentLoyaltyPoint.points,
                        amount: currentLoyaltyPoint.discount_amount,
                      }
                    )}
                  </Text>
                </div>
              </div>
            </Col>
          </Row>
        )}

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={24}>
            <div className="loyalty-info">
              <Text strong>
                {t('customers.loyalty.earnedPointsHistory', 'سجل النقاط المكتسبة وتاريخ الانتهاء')}
              </Text>
              <div className="loyalty-points-history">
                {earnedPoints.length === 0 ? (
                  <Text type="secondary">
                    {t('customers.loyalty.noEarnedPoints', 'لا توجد نقاط مكتسبة لهذا العميل')}
                  </Text>
                ) : (
                  earnedPoints.map((point) => {
                    const usedPoints = Number(point.used_points || 0);
                    const totalPoints = Number(point.points || 0);
                    const availablePoints = Math.max(totalPoints - usedPoints, 0);
                    const isExpired =
                      point.is_expired ||
                      point.expired ||
                      (!!point.expire_at && dayjs(point.expire_at).isBefore(dayjs()));

                    return (
                      <div className="loyalty-point-row" key={point.id}>
                        <div className="loyalty-point-main">
                          <Tag color={isExpired ? 'red' : 'green'}>
                            {isExpired
                              ? t('customers.loyalty.expired', 'منتهية')
                              : t('customers.loyalty.active', 'سارية')}
                          </Tag>
                          <Text>
                            {t('customers.loyalty.earnedPointsValue', 'المكتسب: {{points}}', {
                              points: totalPoints,
                            })}
                            {' - '}
                            {t('customers.loyalty.usedPointsValue', 'المستخدم: {{points}}', {
                              points: usedPoints,
                            })}
                            {' - '}
                            {t('customers.loyalty.availablePointsValue', 'المتبقي: {{points}}', {
                              points: availablePoints,
                            })}
                          </Text>
                        </div>
                        <Text type="secondary">
                          {t('customers.loyalty.expireAt', 'تاريخ الانتهاء')}:&nbsp;
                          {point.expire_at
                            ? dayjs(point.expire_at).format('YYYY-MM-DD')
                            : t('customers.loyalty.noExpiry', 'بدون انتهاء')}
                        </Text>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      <Modal
        title={t('customers.loyalty.editPoints', 'Edit Loyalty Points')}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        destroyOnClose
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="type" label={t('customers.loyalty.customerType', 'Customer Type')}>
            <div className="customer-type-selection">
              <div className="type-columns">
                <div className="type-column">
                  <Radio.Group
                    value={selectedType}
                    onChange={(e) => handleTypeChange(e.target.value)}
                    options={customerTypes}
                  />
                </div>
              </div>
            </div>
          </Form.Item>

          <Form.Item name="category_id" label={t('customers.loyalty.category', 'Category')}>
            <Select
              placeholder={t('customers.loyalty.selectCategory', 'Select a category')}
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
              }
              onChange={(_value) => {
                // Trigger form validation and update
                form.validateFields(['type', 'category_id']);
              }}
            >
              <Option value={null}>{t('customers.loyalty.unassigned', 'غير معين')}</Option>
              {customerCategories.map((category) => (
                <Option key={category.id} value={category.id}>
                  {category.name_ar || category.name_en}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button
              type="default"
              onClick={() => setIsModalVisible(false)}
              style={{ marginRight: 8 }}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button type="primary" htmlType="submit" loading={updateCustomerMutation.isPending}>
              {t('common.save', 'Save')}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CustomerLoyaltyPoints;
